"use client";
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import Image from "next/image";
import { SearchCategories, SearchMerchant } from '@/services/dataTypes';
import { keywordsAction, searchAction } from '@/app/actions';
import { getCategoryHref, getMerchantHref } from '@/constants/hooks';
import { FontAwesomeIcon } from '@/constants/icons';
import { faStore, faTags, faBoxOpen } from '@fortawesome/free-solid-svg-icons';

interface Props {
  companyId: string;
  mer_slug: string;
  slug_type: string;
  cat_slug: string;
}

const SearchBar = ({ companyId, mer_slug, slug_type, cat_slug }: Props) => {
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams);
  const [search, setSearch] = useState<string>(params.get('query') || "");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [tagsData, setTagsData] = useState<any>([]);
  const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([]);
  const [merchantData, setMerchantData] = useState<SearchMerchant[]>([]);
  const [errMsg, setErrMessage] = useState({ show: false, err: false, msg: '' });
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const pathName = usePathname();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  const handleKeywords = async () => {
    const response = await keywordsAction(companyId);
    if (response?.data) setTagsData(response.data);
  };

  useEffect(() => { handleKeywords(); }, [companyId]);

  const handleSearch = async (searchTerm: string) => {
    try {
      if (searchTerm.trim().length >= 3) {
        setLoading(true);
        const response = await searchAction(searchTerm, companyId);
        if (response?.data?.merchants?.length > 0 || response?.data?.categories?.length > 0) {
          setCategoriesData(response.data?.categories || []);
          setMerchantData(response.data?.merchants || []);
          setIsDropdownVisible(!!(pathName !== '/search'));
          setErrMessage({ show: false, err: false, msg: '' });
        } else {
          setMerchantData([]);
          setCategoriesData([]);
          setErrMessage({ show: true, err: true, msg: 'No result found.' });
        }
      } else {
        setMerchantData([]);
        setCategoriesData([]);
        setErrMessage({ show: true, err: true, msg: 'Input should be at least three characters long.' });
      }
    } catch {
      setErrMessage({ show: true, err: true, msg: 'Something went wrong.' });
    } finally {
      setLoading(false);
    }
  };

  const renderMerchant = (merchant: SearchMerchant) => (
    <Link
      key={merchant.merchant_name}
      href={getMerchantHref<SearchMerchant>(merchant, mer_slug, slug_type)}
      className="group flex flex-col items-center justify-center p-2 rounded-xl bg-white/5 hover:bg-[#39ff14]/10 border border-white/10 hover:border-[#39ff14]/30 shadow-sm transition-all duration-300 w-[100px] h-[100px] hover:scale-[1.02] hover:shadow-[0_8px_20px_rgba(57,255,20,0.2)]"
    >
      <div className="h-[50px] w-full flex items-center justify-center mb-1 relative">
        {merchant.merchant_logo ? (
          <Image
            src={merchant.merchant_logo}
            alt={merchant.merchant_name}
            width={70}
            height={40}
            style={{ objectFit: "contain" }}
            className="filter brightness-0 invert opacity-80 group-hover:opacity-100 group-hover:sepia group-hover:saturate-[100] group-hover:hue-rotate-[100deg] transition-all duration-300"
          />
        ) : (
          <span className="text-white/30 text-[10px]">No logo</span>
        )}
      </div>
      <div className="text-center w-full px-1">
        <strong className="text-[11px] text-white/80 group-hover:text-[#39ff14] line-clamp-2 leading-tight transition-colors">
          {merchant.merchant_name}
        </strong>
      </div>
    </Link>
  );

  const renderCategories = (category: SearchCategories) => (
    <Link
      key={category?.name}
      href={getCategoryHref<SearchCategories>(category, cat_slug, slug_type)}
      className="inline-flex no-underline group"
    >
      <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 group-hover:bg-[#39ff14] group-hover:border-[#39ff14] transition-all duration-300 group-hover:scale-105">
        <span className="text-[12px] font-medium text-white/70 group-hover:text-white transition-colors">
          {category?.name}
        </span>
      </div>
    </Link>
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search.trim() === "") {
        setMerchantData([]);
        setCategoriesData([]);
        setErrMessage({ show: false, err: false, msg: '' });
      } else {
        handleSearch(search);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search, companyId]);

  useEffect(() => {
    const query = params.get('query') || "";
    if (query !== search && document.activeElement !== inputRef.current) {
      setSearch(query);
    }
  }, [searchParams]);

  useEffect(() => {
    if (pathName === '/search') setIsDropdownVisible(false);
  }, [pathName]);

  const submitSearchNow = () => {
    if (search.trim()) {
      router.push(`/search?query=${encodeURIComponent(search.trim())}`);
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Animated placeholder text cycling (unique touch)
  const placeholderTexts = ["Search stores...", "Find categories...", "Discover deals...", "Explore brands..."];
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={searchWrapperRef} className="relative w-full max-w-[400px] xl:max-w-[450px]">
      <div className={`
        relative flex items-center rounded-full px-4 py-[8px] 
        bg-gradient-to-r from-[#0a0f0a]/90 via-[#0b110a]/95 to-[#0a0f0a]/90 
        backdrop-blur-xl border transition-all duration-300 w-full
        ${isFocused 
          ? 'border-[#39ff14] shadow-[0_0_0_3px_rgba(57,255,20,0.3),0_8px_20px_rgba(0,0,0,0.5)]' 
          : search.trim() 
            ? 'border-[#39ff14]/70 shadow-[0_4px_12px_rgba(57,255,20,0.1)]' 
            : 'border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.4)]'
        }
      `}>
        <input 
          type="text" 
          placeholder={placeholderTexts[placeholderIndex]}
          className="bg-transparent border-none outline-none text-[13px] w-full text-white/90 placeholder:text-white/40 font-medium z-10 transition-all placeholder:transition-opacity duration-500"
          ref={inputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsDropdownVisible(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            setIsDropdownVisible(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && search.trim()) {
              submitSearchNow();
            }
          }}
          aria-label="Search"
        />
        
        {/* Animated search icon that morphs */}
        <button 
          className="relative group ml-2 z-10 focus:outline-none"
          onClick={submitSearchNow}
          aria-label="Search submit"
        >
          <div className="relative w-4 h-4 flex items-center justify-center">
            {/* Magnifying glass (shown when search empty) */}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={2} 
              stroke="currentColor" 
              className={`
                absolute inset-0 w-4 h-4 transition-all duration-300 text-white/70 group-hover:text-[#39ff14]
                ${search.trim() ? 'opacity-0 scale-50 rotate-0' : 'opacity-100 scale-100 rotate-0'}
              `}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            {/* Arrow right (shown when search has text) */}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={2} 
              stroke="currentColor" 
              className={`
                absolute inset-0 w-4 h-4 transition-all duration-300 text-[#39ff14] group-hover:text-white/90
                ${search.trim() ? 'opacity-100 scale-100 translate-x-0' : 'opacity-0 scale-50 translate-x-2'}
              `}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </div>
        </button>

        {/* Breathing glow ring (CSS only) */}
        {isFocused && (
          <div className="absolute inset-0 rounded-full pointer-events-none animate-pulse-ring" />
        )}

        {isDropdownVisible && (search.trim().length > 0 || tagsData.length > 0) && (
          <div className="absolute left-0 right-0 sm:right-auto top-full mt-3 w-full sm:w-[480px] md:w-[540px] opacity-100 visible transition-all duration-200 z-50 animate-slideDown">
            <div className="bg-gradient-to-br from-[#0b110a]/95 to-[#0a0f0a]/95 backdrop-blur-xl rounded-2xl border border-white/15 shadow-2xl p-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
              
              {loading && search.trim().length >= 3 && merchantData.length === 0 && categoriesData.length === 0 && !errMsg.show && (
                <div className="text-center text-[13px] py-4 text-[#39ff14] font-semibold flex items-center justify-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#39ff14] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-[#39ff14] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-[#39ff14] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  Searching...
                </div>
              )}

              {errMsg.show && search.trim().length > 0 && !loading && (
                <p className={`text-[13px] mb-4 text-center font-medium ${errMsg.err ? 'text-[#39ff14]' : 'text-white/60'}`}>
                  {errMsg.msg}
                </p>
              )}

              {/* Stores / Merchants */}
              {merchantData.length > 0 && !loading && (
                <div className="mb-6 animate-fadeInUp">
                  <p className="text-[13px] font-bold tracking-wide text-white/80 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faStore} className="mr-2 text-[#39ff14] w-3.5 h-3.5" />
                    Stores
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {merchantData.slice(0, 8).map(renderMerchant)}
                  </div>
                </div>
              )}

              {/* Categories */}
              {categoriesData.length > 0 && !loading && (
                <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '50ms' }}>
                  <p className="text-[13px] font-bold tracking-wide text-white/80 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-2 text-[#39ff14] w-3.5 h-3.5" />
                    Categories
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {categoriesData.slice(0, 10).map(renderCategories)}
                  </div>
                </div>
              )}

              {/* Popular Tags */}
              {tagsData.length > 0 && search.trim().length === 0 && (
                <div className="mb-2 animate-fadeInUp" style={{ animationDelay: '100ms' }}>
                  <p className="text-[13px] font-bold tracking-wide text-white/80 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faTags} className="mr-2 text-[#39ff14] w-3.5 h-3.5" />
                    Popular Searches
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tagsData.map((item: string, i: number) => (
                      <Link
                        key={i}
                        href={`/search?query=${item}`}
                        className="px-3 py-1.5 bg-white/5 text-white/70 border border-white/10 text-[12px] font-medium rounded-full hover:bg-[#39ff14] hover:text-white hover:border-[#39ff14] hover:scale-105 transition-all duration-300"
                      >
                        {item}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {search.trim().length >= 3 && !loading && (merchantData.length > 0 || categoriesData.length > 0) && (
                <div className="mt-5 pt-4 border-t border-white/10 text-center animate-fadeInUp">
                  <button 
                    onClick={submitSearchNow}
                    className="text-[13px] font-bold text-[#39ff14] hover:text-white/90 transition-all duration-300 hover:tracking-wider"
                  >
                    View All Results &rarr;
                  </button>
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes pulse-ring {
          0% {
            box-shadow: 0 0 0 0 rgba(57, 255, 20, 0.4);
          }
          70% {
            box-shadow: 0 0 0 6px rgba(57, 255, 20, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(57, 255, 20, 0);
          }
        }
        .animate-pulse-ring {
          animation: pulse-ring 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.3s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default SearchBar;